// Faccina.tsx — il simbolo di Laughdown: una faccia che trattiene la risata.
// Una variante per tema; i colori arrivano dalle variabili del tema.
import type { IdTema } from "../temi/temi.ts";
import { Avatar, type Quale } from "./Avatar.tsx";
import { useAvatar } from "../servizi/account.ts";
import { useState } from "react";
import { facciaCasuale } from "./facciaCasuale.ts";

/** v035b: la faccia di questa pagina, a caso, scelta quando la pagina si apre (poi resta uguale). */
export function useFacciaCasuale(): number {
  const [n] = useState(() => facciaCasuale());
  return n;
}

interface Props {
  tema: IdTema;
  dimensione?: number;
  /** Solo nello stile «laughdown»: chi mostrare. Senza = una faccia a caso (v035b); «io» = l'avatar di chi usa
   * l'app; «mistero» nelle pagine di un altro. */
  quale?: Quale | "io";
}

export function Faccina({ tema, dimensione = 132, quale }: Props) {
  if (tema === "laughdown") {
    // v019: niente faccine 2D nello stile 3D: al loro posto l'avatar scelto da chi usa l'app.
    return <FacciaAvatar dimensione={dimensione} quale={quale} />;
  }
  if (tema === "notte") {
    return (
      <svg width={dimensione} height={dimensione} viewBox="0 0 148 148" aria-hidden="true" className="faccina faccina-notte">
        <circle cx="74" cy="74" r="64" fill="none" stroke="var(--c-primario)" strokeWidth="7" />
        <path d="M44 62 h18 M86 62 h18" stroke="var(--c-primario)" strokeWidth="7" strokeLinecap="round" />
        <path d="M48 98 H100" stroke="var(--c-accento)" strokeWidth="7" strokeLinecap="round" />
      </svg>
    );
  }
  if (tema === "essenziale") {
    // Solo linee, nel colore del testo (tema Silver in bianco e nero).
    return (
      <svg width={dimensione} height={dimensione} viewBox="0 0 132 132" aria-hidden="true" className="faccina">
        <circle cx="66" cy="66" r="58" fill="none" stroke="var(--c-testo)" strokeWidth="5" />
        <path d="M44 56 h10 M78 56 h10" stroke="var(--c-testo)" strokeWidth="6" strokeLinecap="square" />
        <path d="M42 88 H86" stroke="var(--c-testo)" strokeWidth="6" strokeLinecap="square" />
      </svg>
    );
  }
  if (tema === "zucca") {
    // Zucca intagliata che trattiene la risata (tema stagionale d'autunno).
    return (
      <svg width={dimensione} height={dimensione} viewBox="0 0 140 140" aria-hidden="true" className="faccina">
        <path d="M66 20 q2 -10 12 -12" fill="none" stroke="var(--c-accento)" strokeWidth="6" strokeLinecap="round" />
        <ellipse cx="70" cy="78" rx="60" ry="52" fill="var(--c-primario)" />
        <path d="M50 30 q-14 48 0 98 M90 30 q14 48 0 98" fill="none" stroke="var(--c-sfondo)" strokeOpacity="0.25" strokeWidth="4" />
        <path d="M38 66 l12 -14 l12 14z M78 66 l12 -14 l12 14z" fill="var(--c-sfondo)" />
        <path d="M42 96 H90 q8 0 12 -7" fill="none" stroke="var(--c-sfondo)" strokeWidth="7" strokeLinecap="round" />
      </svg>
    );
  }
  if (tema === "fumetto") {
    // Faccina dentro un fumetto (tema Silver).
    return (
      <svg width={dimensione} height={dimensione} viewBox="0 0 140 140" aria-hidden="true" className="faccina">
        <path d="M70 10 C120 10 132 40 132 64 C132 92 108 114 74 114 L46 132 L52 108 C24 100 8 86 8 64 C8 36 28 10 70 10z" fill="var(--c-superficie)" stroke="var(--c-bordo)" strokeWidth="5" strokeLinejoin="round" />
        <circle cx="70" cy="62" r="34" fill="var(--c-primario)" stroke="var(--c-bordo)" strokeWidth="4" />
        <circle cx="58" cy="56" r="5" fill="var(--c-bordo)" />
        <circle cx="82" cy="56" r="5" fill="var(--c-bordo)" />
        <path d="M54 75 H82 q6 0 9 -5" fill="none" stroke="var(--c-bordo)" strokeWidth="5" strokeLinecap="round" />
      </svg>
    );
  }
  if (tema === "caramella") {
    return (
      <svg width={dimensione} height={dimensione} viewBox="0 0 168 168" aria-hidden="true" className="faccina">
        <circle cx="84" cy="84" r="70" fill="var(--c-primario)" />
        <circle cx="44" cy="100" r="20" fill="var(--c-su-primario)" fillOpacity="0.18" />
        <circle cx="124" cy="100" r="20" fill="var(--c-su-primario)" fillOpacity="0.18" />
        <path d="M50 70 q10 -9 20 0 M98 70 q10 -9 20 0" fill="none" stroke="var(--c-su-primario)" strokeWidth="6" strokeLinecap="round" />
        <ellipse cx="84" cy="108" rx="9" ry="6" fill="var(--c-su-primario)" />
        <circle cx="44" cy="96" r="7" fill="var(--c-accento)" />
        <circle cx="124" cy="96" r="7" fill="var(--c-accento)" />
      </svg>
    );
  }
  return (
    <svg width={dimensione} height={dimensione} viewBox="0 0 132 132" aria-hidden="true" className="faccina">
      <circle cx="66" cy="66" r="60" fill="var(--c-accento)" stroke="var(--c-bordo)" strokeWidth="5" />
      <circle cx="46" cy="56" r="7" fill="var(--c-bordo)" />
      <circle cx="86" cy="56" r="7" fill="var(--c-bordo)" />
      <path d="M40 88 H84 q8 0 12 -6" fill="none" stroke="var(--c-bordo)" strokeWidth="6" strokeLinecap="round" />
    </svg>
  );
}

function FacciaAvatar({ dimensione, quale }: { dimensione: number; quale?: Quale | "io" }) {
  const mio = useAvatar();
  const caso = useFacciaCasuale();
  return (
    <span className="faccina ld-faccina" aria-hidden="true">
      <Avatar quale={quale === "io" ? mio : (quale ?? caso)} dimensione={Math.round(dimensione * 0.94)} anello />
    </span>
  );
}
